import React, { useState, useEffect, useRef } from "react";
import { ref, remove, onValue, update } from "firebase/database";
import { database } from "./firebase";
import SignatureCanvas from "react-signature-canvas";
import {
  getStorage,
  ref as storageRef,
  uploadString,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import BossNavigation from "./BossNavigation";
import Navigation from "./Navigation";

export default function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success"); // success or error
  const [saving, setSaving] = useState(false);
  const signPadRef = useRef(null);

  // 🔥 EDITING STATES
  const [editingName, setEditingName] = useState(null); // employee id being edited
  const [editNameValue, setEditNameValue] = useState("");
  const [editingSignature, setEditingSignature] = useState(null); // employee id for signature edit
  const [showSignaturePad, setShowSignaturePad] = useState(false);

  // 🔥 LOAD ALL EMPLOYEES (FIXED)
  useEffect(() => {
    const empRef = ref(database, "employees");

    return onValue(empRef, (snap) => {
      const list = [];

      if (snap.exists()) {
        snap.forEach((child) => {
          list.push({ id: child.key, ...child.val() });
        });
      }

      console.log("ALL EMPLOYEES:", list); // DEBUG
      setEmployees(list);
    });
  }, []);

  const showMsg = (text, type = "success") => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(""), 3000);
  };

  // 🚀 FAST DELETE (PARALLEL)
  const handleDelete = async (emp) => {
    if (saving) return;
    
    if (!window.confirm(`Delete ${emp.name}?`)) return;
    
    setSaving(true);

    try {
      const storage = getStorage();

      // 🔥 DELETE ALL FACE IMAGES FAST
      const deleteTasks = [];

      if (emp.faceImageUrls) {
        emp.faceImageUrls.forEach((_, i) => {
          const fRef = storageRef(storage, `faces/${emp.id}_${i}.jpg`);
          deleteTasks.push(deleteObject(fRef).catch(() => {}));
        });
      }

      // 🔥 DELETE SIGNATURE
      const sigRef = storageRef(storage, `signatures/${emp.id}.png`);
      deleteTasks.push(deleteObject(sigRef).catch(() => {}));

      // 🚀 RUN ALL TOGETHER
      await Promise.all(deleteTasks);

      // 🔥 DELETE DB
      await remove(ref(database, `employees/${emp.id}`));

      showMsg("✅ Employee deleted");

    } catch (err) {
      console.error(err);
      showMsg("❌ Delete failed", "error");
    }

    setSaving(false);
  };

  // ✏️ START EDIT NAME
  const handleStartEditName = (emp) => {
    setEditingName(emp.id);
    setEditNameValue(emp.name);
  };

  // ✏️ CANCEL EDIT NAME
  const handleCancelEditName = () => {
    setEditingName(null);
    setEditNameValue("");
  };

  // 💾 SAVE NAME
  const handleSaveName = async (empId) => {
    if (!editNameValue.trim()) {
      showMsg("Name cannot be empty", "error");
      return;
    }

    setSaving(true);

    try {
      await update(ref(database, `employees/${empId}`), {
        name: editNameValue.trim(),
      });

      showMsg("✅ Name updated");
      setEditingName(null);
    } catch (err) {
      console.error(err);
      showMsg("❌ Update failed", "error");
    }

    setSaving(false);
  };

  // ✍️ START EDIT SIGNATURE
  const handleStartEditSignature = (emp) => {
    setEditingSignature(emp.id);
    setShowSignaturePad(true);
    // Clear previous signature
    setTimeout(() => {
      signPadRef.current?.clear();
    }, 100);
  };

  // ✍️ CANCEL EDIT SIGNATURE
  const handleCancelEditSignature = () => {
    setEditingSignature(null);
    setShowSignaturePad(false);
  };

  // 💾 SAVE SIGNATURE
  const handleSaveSignature = async (emp) => {
    const canvas = signPadRef.current?.getCanvas?.();

    if (!canvas || signPadRef.current.isEmpty()) {
      showMsg("Draw signature", "error");
      return;
    }

    setSaving(true);

    try {
      const storage = getStorage();
      const dataUrl = canvas.toDataURL("image/png");

      // Delete old signature if exists
      if (emp.signatureUrl) {
        const oldSigRef = storageRef(storage, `signatures/${emp.id}.png`);
        await deleteObject(oldSigRef).catch(() => {});
      }

      const sRef = storageRef(storage, `signatures/${emp.id}.png`);
      await uploadString(sRef, dataUrl, "data_url");

      const url = await getDownloadURL(sRef);

      await update(ref(database, `employees/${emp.id}`), {
        signatureUrl: url,
      });

      showMsg("✅ Signature updated");
      setEditingSignature(null);
      setShowSignaturePad(false);

    } catch (err) {
      console.error(err);
      showMsg("❌ Upload failed", "error");
    }

    setSaving(false);
  };

  return (
    <div style={s.page}>
    <Navigation role="hr" />


      <h2 style={s.pageTitle}>Employees</h2>

      {message && (
        <div style={{
          ...s.msg,
          background: messageType === "error" ? "#ef4444" : "#22c55e"
        }}>
          {message}
        </div>
      )}

      {/* 🔥 SHOW ALL EMPLOYEES */}
      <div style={s.grid}>
        {employees.map((emp) => (
          <div key={emp.id} style={s.card}>

            {/* 🔥 SHOW ALL 3 PHOTOS */}
            <div style={s.imageRow}>
              {emp.faceImageUrls?.map((img, i) => (
                <img key={i} src={img} style={s.face} alt="face" />
              ))}
            </div>

            {/* ✏️ NAME - EDITABLE */}
            {editingName === emp.id ? (
              <div style={s.editNameContainer}>
                <input
                  style={s.nameInput}
                  value={editNameValue}
                  onChange={(e) => setEditNameValue(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSaveName(emp.id)}
                  autoFocus
                />
                <div style={s.editNameActions}>
                  <button 
                    style={s.saveBtn} 
                    onClick={() => handleSaveName(emp.id)}
                    disabled={saving}
                  >
                    ✓ Save
                  </button>
                  <button 
                    style={s.cancelBtn} 
                    onClick={handleCancelEditName}
                    disabled={saving}
                  >
                    ✕ Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={s.nameRow}>
                <h3 style={s.name}>{emp.name}</h3>
                <button 
                  style={s.editIconBtn} 
                  onClick={() => handleStartEditName(emp)}
                  title="Edit name"
                >
                  ✏️
                </button>
              </div>
            )}

            <p style={s.sub}>{emp.id}</p>

            {/* ✍️ SIGNATURE - EDITABLE */}
            {editingSignature === emp.id && showSignaturePad ? (
              <div style={s.signatureEditContainer}>
                <div style={s.signaturePadWrapper}>
                  <SignatureCanvas
                    ref={signPadRef}
                    canvasProps={{
                      width: 300,
                      height: 120,
                      style: {
                        width: "100%",
                        borderRadius: "8px",
                        background: "white",
                      }
                    }}
                    penColor="black"
                    backgroundColor="white"
                  />
                </div>
                <div style={s.editNameActions}>
                  <button 
                    style={s.saveBtn} 
                    onClick={() => handleSaveSignature(emp)}
                    disabled={saving}
                  >
                    💾 Save Signature
                  </button>
                  <button 
                    style={s.cancelBtn} 
                    onClick={handleCancelEditSignature}
                    disabled={saving}
                  >
                    ✕ Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={s.signatureRow}>
                {emp.signatureUrl ? (
                  <div style={s.signatureDisplay}>
                    <img src={emp.signatureUrl} style={s.sign} alt="sign" />
                    <button 
                      style={s.editIconBtn} 
                      onClick={() => handleStartEditSignature(emp)}
                      title="Edit signature"
                    >
                      ✏️
                    </button>
                  </div>
                ) : (
                  <button 
                    style={s.addSignatureBtn}
                    onClick={() => handleStartEditSignature(emp)}
                  >
                    ✍️ Add Signature
                  </button>
                )}
              </div>
            )}

            {/* 🗑️ DELETE BUTTON */}
            <button 
              style={s.deleteBtn}
              onClick={() => handleDelete(emp)} 
              disabled={saving}
            >
              🗑️ Delete
            </button>

          </div>
        ))}
      </div>
    </div>
  );
}

// 🎨 MOBILE UI
const s = {
  page: {
    background: "#0f172a",
    minHeight: "100vh",
    color: "white",
    padding: 10,
  },

  pageTitle: {
    textAlign: "center",
    fontSize: "24px",
    marginBottom: "20px",
    color: "#f1f5f9",
  },

  grid: {
    display: "grid",
    gap: 12,
    maxWidth: "500px",
    margin: "0 auto",
  },

  card: {
    background: "#1e293b",
    padding: 16,
    borderRadius: 12,
    textAlign: "center",
    border: "1px solid rgba(148, 163, 184, 0.1)",
  },

  imageRow: {
    display: "flex",
    gap: 8,
    justifyContent: "center",
    marginBottom: 12,
  },

  face: {
    width: 65,
    height: 65,
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid #3b82f6",
  },

  // ✏️ NAME STYLES
  nameRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginBottom: 4,
  },

  name: {
    margin: 0,
    fontSize: "18px",
    color: "#f1f5f9",
  },

  editIconBtn: {
    background: "rgba(59, 130, 246, 0.2)",
    border: "1px solid rgba(59, 130, 246, 0.3)",
    borderRadius: "6px",
    color: "#60a5fa",
    cursor: "pointer",
    fontSize: "14px",
    padding: "4px 8px",
    transition: "all 0.2s",
  },

  editNameContainer: {
    marginBottom: 8,
  },

  nameInput: {
    width: "100%",
    padding: "10px",
    background: "rgba(51, 65, 85, 0.5)",
    border: "2px solid #3b82f6",
    borderRadius: "8px",
    color: "white",
    fontSize: "16px",
    outline: "none",
    marginBottom: "8px",
    boxSizing: "border-box",
    textAlign: "center",
  },

  editNameActions: {
    display: "flex",
    gap: 8,
    justifyContent: "center",
  },

  saveBtn: {
    padding: "8px 16px",
    background: "#22c55e",
    border: "none",
    borderRadius: "8px",
    color: "white",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
  },

  cancelBtn: {
    padding: "8px 16px",
    background: "rgba(239, 68, 68, 0.2)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: "8px",
    color: "#fca5a5",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
  },

  sub: {
    fontSize: 11,
    color: "#64748b",
    margin: "4px 0 12px 0",
  },

  // ✍️ SIGNATURE STYLES
  signatureRow: {
    marginBottom: 12,
  },

  signatureDisplay: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },

  sign: {
    height: 40,
    background: "white",
    padding: "4px 8px",
    borderRadius: "6px",
  },

  addSignatureBtn: {
    padding: "10px 20px",
    background: "rgba(59, 130, 246, 0.15)",
    border: "2px dashed rgba(59, 130, 246, 0.4)",
    borderRadius: "8px",
    color: "#60a5fa",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    transition: "all 0.2s",
  },

  signatureEditContainer: {
    marginBottom: 12,
  },

  signaturePadWrapper: {
    background: "white",
    borderRadius: "8px",
    padding: "4px",
    marginBottom: "10px",
    border: "2px solid #3b82f6",
  },

  // 🗑️ DELETE BUTTON
  deleteBtn: {
    width: "100%",
    padding: "10px",
    background: "rgba(239, 68, 68, 0.15)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: "8px",
    color: "#fca5a5",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    marginTop: 4,
  },

  // 📢 MESSAGE
  msg: {
    padding: "12px 16px",
    borderRadius: "10px",
    marginBottom: 16,
    textAlign: "center",
    fontWeight: "600",
    fontSize: "14px",
    maxWidth: "500px",
    margin: "0 auto 16px auto",
  },
};